import Text from "common/Typography/Text.tsx";
import styled, { css, useTheme } from "styled-components";
import { formatMoney } from "features/settings/Subscription/NewLook/formatMoney.ts";
import Flex from "common/Boxes/Flex";
import { MOBILE_SEARCH } from "@shared/common/constants.ts";
import { useUser } from "@shared/utils/hooks/misc.ts";
import {
  DURATION_TO_PRIMARY_DISPLAY,
  DURATION_TO_SECONDARY_DISPLAY,
} from "features/settings/common/types.ts";
import React, { useContext } from "react";
import { CancelSubscriptionContext } from "@shared/features/settings/subscription/CancelSubscription.context.ts";
import { toFixed } from "@shared/utils/numbers.ts";
import { Tag } from "features/settings/Subscription/DurationRadio.tsx";
import {
  Title,
  Description,
  newLookPanel,
} from "features/settings/CancelSubscription/styles";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";

const LoseDiscount = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const theme = useTheme();
  const { user } = useUser();
  const { currentDiscount, plan } = useContext(CancelSubscriptionContext)!;

  const userDuration = user?.package_duration ?? 30;
  const durationDisplay = user?.package_duration
    ? t(DURATION_TO_SECONDARY_DISPLAY[user?.package_duration])
    : undefined;
  const durationSecondaryDisplay = user?.package_duration
    ? t(DURATION_TO_PRIMARY_DISPLAY[user?.package_duration])
    : undefined;
  const originalPlanPrice = (plan?.[30]?.price || 0) * (userDuration / 30);
  const currentDiscountAmount = originalPlanPrice * (currentDiscount || 0);
  const currentPrice = toFixed(originalPlanPrice - currentDiscountAmount, 2);
  const discountPercent = toFixed((currentDiscount || 0) * 100, 1);

  return (
    <Wrapper>
      <Title>Don't lose your {discountPercent}% discount</Title>
      <Description style={{ marginBottom: 0 }}>
        {t("cancel_subscription.special_monthly_rate")}
      </Description>
      <Description style={{ marginBottom: 16 }}>
        {t("cancel_subscription.discount_no_longer_available")}
      </Description>
      <Card>
        <Flex>
          <div>
            <Text type="secondary">{durationDisplay}</Text>
            <Plan>{user?.package}</Plan>
          </div>
          <PriceWrapper>
            <Price>
              ${theme.isNewLook ? formatMoney(currentPrice) : currentPrice}{" "}
            </Price>
            <Text type="secondary">/ {durationSecondaryDisplay}</Text>
          </PriceWrapper>
        </Flex>
        <DiscountWrapper>
          <Flex>
            {t("cancel_subscription.save_a_year", {
              amount: theme.isNewLook
                ? formatMoney((currentDiscountAmount * 360) / userDuration)
                : toFixed((currentDiscountAmount * 360) / userDuration, 2),
            })}
            {/* The title already names the discount; no pill repeats it. */}
            {!theme.isNewLook && (
              <Tag style={{ padding: "4px 8px", marginLeft: 12 }}>
                -{discountPercent}% OFF
              </Tag>
            )}
          </Flex>
        </DiscountWrapper>
      </Card>
      <Description style={{ marginBottom: 16 }}>
        {t("cancel_subscription.let_go_special_discount")}
      </Description>
    </Wrapper>
  );
};

export default LoseDiscount;

const Wrapper = styled.div`
  ${(p) =>
    p.theme.isNewLook &&
    css`
      ${newLookPanel}
      /* The closing question sits on the panel padding, like the title. */
      > :last-child {
        margin-bottom: 0 !important;
      }
    `}
`;
const Card = styled.div`
  max-width: 515px;
  padding: 24px;
  border-radius: 16px;
  border: 1px solid ${(p) => p.theme.colors.primaryBlue};
  margin: 40px 0;
  @media (max-width: ${MOBILE_SEARCH}) {
    margin: 20px 0;
  }
  box-shadow: 0 6px 24px 0 #3272fe1f;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* The plan at its discounted price, in a well. */
      max-width: none;
      margin: 4px 0 20px;
      padding: 16px;
      border: none;
      border-radius: ${p.theme.radii.control}px;
      background: ${getNewLookTokens(p.theme).well};
      box-shadow: inset 0 0 0 1px ${getNewLookTokens(p.theme).hairline};
      font-variant-numeric: tabular-nums;
    `}
`;
const PriceWrapper = styled.div`
  display: flex;
  align-items: baseline;
`;
const Price = styled(Text)`
  font-size: 40px;
  margin-right: 4px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      && {
        ${newLookType.display};
        color: ${getNewLookTokens(p.theme).ink};
      }
    `}
`;
const Plan = styled(Text)`
  font-size: 22px;
  font-weight: 500;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      && {
        display: block;
        ${newLookType.panelTitle};
        color: ${getNewLookTokens(p.theme).ink};
      }
    `}
`;
const DiscountWrapper = styled.div`
  padding: 8px 10px;
  display: flex;
  justify-content: center;
  color: ${(p) => p.theme.colors.primaryBlue};
  background-color: ${(p) => p.theme.colors.lightBlue};
  font-weight: 500;
  margin-top: 20px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* The yearly saving as a quiet line under the price. */
      justify-content: flex-start;
      margin-top: 8px;
      padding: 0;
      background: transparent;
      ${newLookType.label};
      color: ${getNewLookTokens(p.theme).up};
    `}
`;
